import React, { useEffect, useState, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useNavigate, Link } from "react-router-dom";
import { useFormData } from "@/stores/formStore";
import Footer from "@/components/ink-insanity/Footer";
import LanguageSwitcher from "@/components/ink-insanity/LanguageSwitcher";
import LanguageHint from "@/components/ink-insanity/LanguageHint";
import FeedbackTab from "@/components/ink-insanity/FeedbackTab";

import SEO from "@/components/ink-insanity/SEO";
import { Progress } from "@/components/ui/progress";
import heroWaves from "@/assets/hero-waves.webp";
import { usePrefetchDicePage, prefetchDiceChunk } from "@/lib/prefetch-dice";

import { Users, Brain, BookOpen, ChevronRight, Clock, Dices } from "lucide-react";
import { Ghost, Icon, type LucideProps } from "lucide-react";
import { pumpkin } from "@lucide/lab";
import { useTranslation, Trans } from "react-i18next";
import { AuroraBackground } from "@/components/ink-insanity/AuroraBackground";
import EraCard from "@/components/ink-insanity/EraCard";
import QuickStartFab from "@/components/ink-insanity/QuickStartFab";
import { HOMEPAGE_ERAS, type HomepageEra } from "@/config/home-era-list";
import LogoWithBurst from "@/components/ink-insanity/LogoWithBurst";
import { allEntries, localizeEntries } from "@/utils/changelog-i18n";
import { Skeleton } from "@/components/ui/skeleton";

// --- Chaos Index: deterministic daily value seeded by date ---
function getDailySanity(): number {
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  // Simple hash
  let h = seed;
  h = ((h >> 16) ^ h) * 0x45d9f3b;
  h = ((h >> 16) ^ h) * 0x45d9f3b;
  h = (h >> 16) ^ h;
  return Math.abs(h) % 101; // 0–100
}

function getSanityFlavor(value: number, t: (k: string) => string): { label: string; color: string } {
  if (value <= 15) return { label: t("sanity.level1"), color: "hsl(0, 70%, 50%)" };
  if (value <= 30) return { label: t("sanity.level2"), color: "hsl(15, 70%, 50%)" };
  if (value <= 50) return { label: t("sanity.level3"), color: "hsl(40, 70%, 50%)" };
  if (value <= 70) return { label: t("sanity.level4"), color: "hsl(60, 50%, 45%)" };
  if (value <= 85) return { label: t("sanity.level5"), color: "hsl(140, 40%, 45%)" };
  return { label: t("sanity.level6"), color: "hsl(160, 45%, 45%)" };
}

// --- Animated counter hook ---
function useAnimatedCounter(target: number, duration = 1500): number {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return count;
}

// --- Fetch global investigator count from Supabase ---
function useInvestigatorCount(): number | null {
  const [target, setTarget] = useState<number | null>(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await (supabase as any)
          .from("investigator_counter")
          .select("count")
          .eq("id", 1)
          .single();
        if (!error && data) {
          setTarget(Number(data.count));
        } else {
          setTarget(127);
        }
      } catch {
        setTarget(127);
      }
    };
    fetchCount();
  }, []);

  return target;
}

// --- Wavy M3-style progress bar ---
const WavyProgressBar: React.FC<{ value: number; color: string }> = ({ value, color }) => {
  const width = 200;
  const height = 20;
  const amplitude = 4;
  const wavelength = 32;
  const strokeW = 3.5;
  const filledWidth = (value / 100) * width;

  const [isHovered, setIsHovered] = useState(false);
  const [phaseOffset, setPhaseOffset] = useState(0);
  const speedRef = useRef(0);
  const animRef = useRef<number>();
  const lastTimeRef = useRef(performance.now());

  const targetSpeed = isHovered ? 30 : 0;
  const easeRate = 3; // how fast speed ramps up/down per second

  useEffect(() => {
    lastTimeRef.current = performance.now();
    const tick = (now: number) => {
      const dt = Math.min((now - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = now;
      // Ease speed toward target
      const diff = targetSpeed - speedRef.current;
      speedRef.current += diff * Math.min(easeRate * dt * 5, 1);
      if (Math.abs(speedRef.current) < 0.01 && targetSpeed === 0) {
        speedRef.current = 0;
        // Stop the loop when fully at rest
        animRef.current = undefined;
        return;
      }
      setPhaseOffset(prev => prev + dt * speedRef.current);
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isHovered, targetSpeed]);

  const buildWavyPath = (startX: number, endX: number) => {
    const midY = height / 2;
    let d = `M ${startX} ${midY}`;
    for (let x = startX; x <= endX; x += 1) {
      const y = midY + Math.sin(((x - startX + phaseOffset) / wavelength) * Math.PI * 2) * amplitude;
      d += ` L ${x} ${y}`;
    }
    return d;
  };

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="overflow-visible"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <path d={buildWavyPath(0, width)} fill="none" stroke="rgba(80,80,80,1)" strokeWidth={strokeW} strokeLinecap="butt" />
      {filledWidth > 0 && (
        <path d={buildWavyPath(0, filledWidth)} fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="butt" />
      )}
    </svg>
  );
};

const MORPH_PHRASES_BASE = [
  "Roll for Sanity",
  "Confront the Unknown",
  "Embrace the Madness",
  "The Stars Are Right",
  "That Is Not Dead",
  "Ph'nglui Mglw'nafh",
  "Do You Dare Look?",
  "Sanity Is Optional",
  "Trust No One",
  "The Void Stares Back",
  "What Was That Sound?",
  "Your Luck Runs Out",
  "Iä! Iä! Cthulhu Fhtagn!",
];

const shuffleArray = <T,>(arr: T[]): T[] => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const DICE_TYPES = [4, 6, 8, 10, 12, 20, 100];

const MorphTagline: React.FC = () => {
  const [phrases] = useState(() => shuffleArray(MORPH_PHRASES_BASE));
  const [index, setIndex] = useState(0);
  const [morph, setMorph] = useState(0);
  const animRef = useRef<number>();
  const cooldownRef = useRef(0);
  const diceBoxRef = useRef<any>(null);
  const [diceReady, setDiceReady] = useState(false);

  const currentPhrase = phrases[index % phrases.length];
  const nextPhrase = phrases[(index + 1) % phrases.length];

  // Lazy-load the dice engine on mount
  useEffect(() => {
    let mounted = true;
    let cleanupResize: (() => void) | undefined;

    const loadDice = async () => {
      try {
        const { default: DiceBox } = await import("@3d-dice/dice-box");

        const box = new DiceBox({
          assetPath: "/dice-box-assets/",
          container: "#home-dice-container",
          id: "home-dice-canvas",
          gravity: 1,
          scale: 6,
          throwForce: 6,
          spinForce: 5,
          lightIntensity: 1,
          enableShadows: true,
          themeColor: "#7abb8f",
          onRollComplete: () => {
            setTimeout(() => {
              if (box.canvas) {
                box.canvas.style.transition = "opacity 0.8s ease-out";
                box.canvas.style.opacity = "0";
                // After fade, clear dice and reset opacity for next roll
                setTimeout(() => {
                  box.clear();
                  if (box.canvas) {
                    box.canvas.style.transition = "";
                    box.canvas.style.opacity = "1";
                  }
                }, 800);
              }
            }, 5000);
          },
        });

        const syncSize = () => {
          if (box.canvas) {
            const w = window.innerWidth;
            const h = window.innerHeight;
            Object.assign(box.canvas.style, {
              position: "fixed",
              left: "0",
              top: "0",
              width: `${w}px`,
              height: `${h}px`,
              pointerEvents: "none",
              zIndex: "50",
            });
          }
        };

        syncSize();
        await box.init();
        syncSize();
        box.resizeWorld();

        const handleResize = () => {
          syncSize();
          box.resizeWorld();
        };
        window.addEventListener("resize", handleResize);

        if (mounted) {
          diceBoxRef.current = box;
          setDiceReady(true);
        }

        cleanupResize = () => window.removeEventListener("resize", handleResize);
      } catch {
        // Dice engine failed to load — silently degrade
      }
    };

    loadDice();
    return () => {
      mounted = false;
      cleanupResize?.();
    };
  }, []);

  // Morph animation
  useEffect(() => {
    const morphTime = 4.5;
    const cooldownTime = 15;
    let lastTime = performance.now();

    const animate = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (cooldownRef.current > 0) {
        cooldownRef.current -= dt;
        if (cooldownRef.current <= 0) cooldownRef.current = 0;
      } else {
        setMorph((prev) => {
          const next = prev + dt / morphTime;
          if (next >= 1) {
            cooldownRef.current = cooldownTime;
            setIndex((i) => i + 1);
            return 0;
          }
          return next;
        });
      }
      animRef.current = requestAnimationFrame(animate);
    };

    cooldownRef.current = cooldownTime;
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  const handleClick = useCallback(() => {
    if (!diceReady || !diceBoxRef.current) return;

    const sides = DICE_TYPES[Math.floor(Math.random() * DICE_TYPES.length)];
    const qty = Math.floor(Math.random() * 4) + 1; // 1-4 dice
    diceBoxRef.current.roll(`${qty}d${sides}`);
  }, [diceReady]);

  const isMorphing = morph > 0.01 && morph < 0.99;
  const text1Blur = Math.min(8 * morph, 8);
  const text2Blur = Math.min(8 * (1 - morph), 8);

  return (
    <>
      {/* Hidden dice container — canvas gets repositioned to fixed overlay */}
      <div id="home-dice-container" className="fixed inset-0 pointer-events-none z-50" style={{ width: 0, height: 0, overflow: "visible" }} />

      <div
        className={cn(
          "relative h-[32px] md:h-[40px] whitespace-nowrap",
          diceReady && "cursor-pointer"
        )}
        style={{ filter: isMorphing ? "url(#threshold) blur(0.4px)" : "none" }}
        onClick={handleClick}
        role={diceReady ? "button" : undefined}
        tabIndex={diceReady ? 0 : undefined}
        aria-label={diceReady ? "Click to roll dice" : undefined}
        onKeyDown={(e) => { if (diceReady && (e.key === "Enter" || e.key === " ")) handleClick(); }}
      >
        <p
          className="absolute inset-0 flex items-center justify-center text-2xl md:text-3xl font-bold text-white font-antiqua tracking-tight"
          style={{ opacity: 1 - morph, filter: `blur(${text1Blur}px)` }}
        >
          {currentPhrase}
        </p>
        <p
          className="absolute inset-0 flex items-center justify-center text-2xl md:text-3xl font-bold text-white font-antiqua tracking-tight"
          style={{ opacity: morph, filter: `blur(${text2Blur}px)` }}
          aria-hidden="true"
        >
          {nextPhrase}
        </p>
      </div>
    </>
  );
};

const HeroSection: React.FC = () => {
  const { t, i18n } = useTranslation("home");
  const { t: tNav } = useTranslation("nav");
  return (
    <section className="text-center pt-16 pb-10 max-w-2xl mx-auto animate-fade-in relative z-10">
      <div className="group mx-auto mb-4 w-fit">
        <LogoWithBurst
          src="/lovable-uploads/42db7210-adf1-4d43-9443-969c94fa2bb2.png"
          alt={tNav("logoAlt")}
          size={80}
        />
      </div>
      <h1 className="text-5xl md:text-6xl font-bold text-white font-antiqua tracking-tight mb-3">
        {t("appName")}
      </h1>

      <p className="text-lg text-muted-foreground font-antiqua leading-relaxed">
        {t("tagline")}{" "}
        <br className="hidden md:block" />
        {t("taglineSub")}
      </p>
    </section>
  );
};

// --- Spooky Season banner ---
const PumpkinIcon = React.forwardRef<SVGSVGElement, LucideProps>((props, ref) => (
  <Icon ref={ref} iconNode={pumpkin} {...props} />
));
PumpkinIcon.displayName = "PumpkinIcon";

const MILESTONE_EXPIRES = new Date("2026-11-30T23:59:59Z");

const MilestoneBanner: React.FC = () => {
  const { t } = useTranslation("home");
  const [expired, setExpired] = useState(() => new Date() >= MILESTONE_EXPIRES);

  useEffect(() => {
    if (expired) return;
    const ms = MILESTONE_EXPIRES.getTime() - Date.now();
    if (ms <= 0) {
      setExpired(true);
      return;
    }
    const timer = setTimeout(() => setExpired(true), Math.min(ms, 2_147_483_000));
    return () => clearTimeout(timer);
  }, [expired]);

  if (expired) return null;

  return (
    <div
      role="status"
      className="animate-slide-fade-in relative z-10 w-full max-w-[960px] mb-8 rounded-full border border-brand-green/40 bg-gradient-to-r from-brand-green/15 via-brand-cream/10 to-brand-green/15 backdrop-blur-sm px-5 py-2.5 flex items-center justify-center gap-2 text-center"
    >
      <PumpkinIcon className="w-4 h-4 text-brand-green shrink-0" aria-hidden="true" />
      <p className="text-sm md:text-base text-foreground font-antiqua font-sans">
        <Trans i18nKey="milestoneBanner" components={{ bold: <strong /> }} t={t} />
      </p>
      <Ghost className="w-4 h-4 text-brand-green shrink-0" aria-hidden="true" />
    </div>
  );
};

// --- NEW badge helpers ---
const DISMISSED_KEY = "dismissedNewBadges";

function getDismissedBadges(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
}

function dismissBadge(eraId: string) {
  const dismissed = getDismissedBadges();
  if (!dismissed.includes(eraId)) {
    dismissed.push(eraId);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  }
}

function shouldShowNewBadge(eraId: string, newUntil?: string): boolean {
  if (!newUntil) return false;
  if (new Date() >= new Date(newUntil)) return false;
  return !getDismissedBadges().includes(eraId);
}

const eras = HOMEPAGE_ERAS;
const typeBadgeStyles: Record<string, string> = {
  FEATURE: "bg-emerald-900/60 text-emerald-300 border-emerald-700/50",
  IMPROVEMENT: "bg-amber-900/60 text-amber-300 border-amber-700/50",
  RELEASE: "bg-purple-900/60 text-purple-300 border-purple-700/50",
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { setEra } = useFormData();
  const { t, i18n } = useTranslation("home");
  const { t: tNav } = useTranslation("nav");
  usePrefetchDicePage();

  const sanity = getDailySanity();
  const flavor = getSanityFlavor(sanity, t);
  const investigatorTarget = useInvestigatorCount();
  const investigatorCount = useAnimatedCounter(investigatorTarget ?? 0, 2000);
  const latestChangelog = localizeEntries(allEntries.slice(0, 3), i18n.language);

  // Fade in hero wave image once loaded
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const onHeroLoad = useCallback(() => setHeroLoaded(true), []);

  const handleEraSelect = (era: HomepageEra) => {
    if (!era.available) return;
    dismissBadge(era.id);
    setEra(era.id);
    navigate(era.route);
  };

  const scrollToEraGrid = useCallback(() => {
    const el = document.getElementById("home-era-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <>
      <SEO
        description={t("seoDescription")}
        path="/"
      />
      <div className="bg-background overflow-hidden min-h-screen flex flex-col">
        
        <main className="bg-subtle-pattern flex w-full flex-col items-center px-6 max-md:px-5 flex-grow relative">
          {/* Top-left dice roller — mirrors the language switcher's placement and style */}
          <div className="absolute top-4 left-5 z-30 md:left-8 flex flex-col items-start">
            <Link
              to="/dice"
              onMouseEnter={prefetchDiceChunk}
              onFocus={prefetchDiceChunk}
              onTouchStart={prefetchDiceChunk}
              aria-label={tNav("diceRoller")}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground/80 hover:text-white hover:bg-secondary transition-colors"
            >
              <Dices className="h-4 w-4" />
              <span className="font-sans hidden md:inline">{tNav("diceRoller")}</span>
            </Link>
          </div>
          {/* Top-right language switcher — mirrors global Header padding (py-4 px-5 / md:px-8) */}
          <div className="absolute top-4 right-5 z-30 md:right-8 flex flex-col items-end gap-2">
            <LanguageSwitcher />
            <LanguageHint />
          </div>
          {/* Aurora background */}
          <AuroraBackground className="absolute top-0 left-0 right-0 h-[750px] pointer-events-none z-0" showRadialGradient={true} />

          {/* Hero wave background */}
          <div className="absolute top-0 left-0 right-0 h-[750px] pointer-events-none z-[1]" style={{ maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)' }}>
            <img
              src={heroWaves}
              alt=""
              className="w-full h-full object-cover object-center opacity-40 saturate-150"
              width={1920}
              height={768}
              loading="eager"
              fetchPriority="high"
              onLoad={onHeroLoad}
            />
          </div>

          {/* SVG filter for morph effect */}
          <svg className="absolute w-0 h-0">
            <defs>
              <filter id="threshold">
                <feColorMatrix
                  in="SourceGraphic"
                  type="matrix"
                  values="1 0 0 0 0
                          0 1 0 0 0
                          0 0 1 0 0
                          0 0 0 255 -140"
                />
              </filter>
            </defs>
          </svg>

          <HeroSection />

          <MilestoneBanner />

          {/* Stat Modules */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full max-w-[960px] mb-14 relative z-10">
            {/* Investigators Created */}
            <div className="animate-slide-fade-in rounded-xl border border-border bg-card/95 backdrop-blur-sm p-5 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-brand-green" />
              </div>
              <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-1 font-antiqua">
                {t("stats.investigatorsCreated")}
              </p>
              {investigatorTarget === null ? (
                <Skeleton className="h-12 w-24 rounded-md" />
              ) : (
                <p className="text-5xl font-bold text-white font-antiqua tabular-nums">
                  {investigatorCount.toLocaleString()}
                </p>
              )}
            </div>

            {/* Chaos Index */}
            <div className="animate-slide-fade-in rounded-xl border border-border bg-card/95 backdrop-blur-sm p-5 flex flex-col items-center text-center" style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}>
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
                <Brain className="w-5 h-5 text-brand-green" />
              </div>
              <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-1 font-antiqua">
                {t("stats.dailyChaosIndex")}
              </p>
              <div className="w-full mt-1 mb-2">
                <WavyProgressBar value={sanity} color={flavor.color} />
              </div>
              <p className="text-sm text-foreground/80 italic">
                {flavor.label}
              </p>
            </div>

            {/* The Grimoire */}
            <div className="animate-slide-fade-in rounded-xl border border-border bg-card/95 backdrop-blur-sm p-5" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
              <Link to="/changelog" className="flex items-center gap-2 mb-3 group/header hover:opacity-80 transition-opacity">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-brand-green" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-antiqua group-hover/header:text-white transition-colors">
                    {t("stats.casefile")}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("stats.latestUpdates")}</p>
                </div>
              </Link>
              <ul className="space-y-2.5">
                {latestChangelog.map((entry, index) => {
                  const dateBadgeColors = [
                    "bg-brand-green/15 text-brand-green border-brand-green/30",
                    "bg-brand-cream/15 text-brand-cream border-brand-cream/30",
                    "bg-era-gaslight/15 text-era-gaslight border-era-gaslight/30",
                  ];
                  const badgeColor = dateBadgeColors[index % dateBadgeColors.length];

                  return (
                    <li key={entry.slug}>
                      <Link
                        to={`/changelog/${entry.slug}`}
                        className="group flex items-center gap-2 hover:bg-[rgba(50,50,50,0.5)] rounded-lg p-1.5 -m-1.5 transition-colors"
                      >
                        <span
                          className={`text-[11px] uppercase tracking-wider font-semibold border rounded px-1.5 py-0.5 whitespace-nowrap ${badgeColor}`}
                        >
                          {entry.date}
                        </span>
                        <span className="text-sm text-foreground/80 leading-tight group-hover:text-white transition-colors line-clamp-2">
                          {entry.title}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          {/* Era Selection */}
          <section id="home-era-grid" className="w-full max-w-[960px] mb-16 relative z-10">
            <div className="text-center mb-8 animate-slide-fade-in" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
              <h2 className="text-2xl font-bold text-white font-antiqua mb-1">
                {t("chooseEra")}
              </h2>
              <p className="text-lg text-muted-foreground font-antiqua">
                {t("eraSection.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {eras.map((era, i) => (
                <EraCard
                  key={era.id}
                  title={t(era.titleKey)}
                  subtitle={t(era.subtitleKey)}
                  description={t(era.descriptionKey)}
                  image={era.image}
                  icon={era.icon}
                  iconColor={era.iconColor}
                  available={era.available}
                  showNewBadge={shouldShowNewBadge(era.id, era.newUntil)}
                  wide={i === 0 && eras.length % 2 !== 0}
                  imagePosition={era.imagePosition}
                  onClick={() => handleEraSelect(era)}
                  animationDelay={`${400 + i * 100}ms`}
                />
              ))}
            </div>

            {/* Coming Soon teaser card */}
            <div className="mt-8 rounded-xl border border-dashed border-border bg-card/60 backdrop-blur-sm p-8 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                  <Clock className="w-5 h-5 text-brand-cream" />
                </div>
                <h3 className="text-xl font-bold text-brand-cream font-antiqua">
                  {t("eraSection.comingSoon")}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
                {t("eraSection.comingSoonBody")}
              </p>
            </div>
          </section>

          {/* Bottom morph tagline */}
          <section className="w-full max-w-[960px] mb-16 relative z-10 flex justify-center">
            <MorphTagline />
          </section>
        </main>
        <FeedbackTab externalOpen={feedbackOpen} onExternalOpenChange={setFeedbackOpen} hideTab />
        <QuickStartFab
          eras={eras}
          shouldShowNewBadge={shouldShowNewBadge}
          onSelect={handleEraSelect}
          onBrowseAll={scrollToEraGrid}
        />
        <Footer onFeedbackOpen={() => setFeedbackOpen(true)} />
      </div>
    </>
  );
};

export default Home;
